/**
 * RelentlessSkillEffect — Conjuration Swarm path terminal passive.
 *
 * Always-on passive. 3 seconds after any wisp dies in combat (not expiry,
 * not Detonate, not Convergence), a replacement wisp spawns automatically
 * at the player's current position.
 *
 * Mechanic:
 * - `onWispDied(wispId, deathCause)` — called when a wisp dies. If
 *   deathCause === 'combat', enqueues a respawn timer for autoRespawnDelayMs.
 * - On each `update(delta)`, all pending timers count down. When a timer
 *   expires, attempts to spawn one wisp at the player's current position —
 *   but only if wispCount < maxWisps at that moment. If the cap is full,
 *   the respawn is silently skipped.
 * - Timers are per-wisp: 3 rapid combat deaths enqueue 3 independent timers.
 * - Auto-respawn delay uses min aggregation: a lower delay is the upgrade
 *   outcome. Resolved at death time from UpgradeEffectResolver.
 *
 * UpgradeEffectResolver key (conjuration tree scope):
 * - autoRespawnDelayMs: min aggregation, base 3000ms
 *
 * Does NOT fire on Detonate kills (deathCause: 'detonate'), natural expiry
 * (deathCause: 'expiry'), or Convergence kills (deathCause: 'converge').
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Relentless
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RelentlessDeps {
  readonly resolver: {
    /**
     * Returns the minimum value for a key across active upgrades, or
     * undefined if no upgrade contributes to this key.
     */
    getMinEffect(skillId: string, key: string): number | undefined;
  };
  readonly summons: {
    /** Returns the number of currently living wisps. */
    getWispCount(): number;
    /** Returns the current wisp cap (maxWisps). */
    getMaxWisps(): number;
    /**
     * Spawn a single replacement wisp at (x, y) with standard wisp config.
     * Returns the new wisp's ID.
     */
    spawnWisp(x: number, y: number): string;
  };
  readonly player: {
    /** Returns the player's current world position in pixels. */
    getPosition(): { x: number; y: number };
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface RelentlessConfig {
  /** Base delay between a wisp's combat death and its replacement spawning. */
  readonly autoRespawnDelayMs: number;
}

const DEFAULT_CONFIG: RelentlessConfig = {
  autoRespawnDelayMs: 3000, // spec § Relentless: 3-second respawn window
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RelentlessSkillEffect implements SkillEffect {
  readonly skillId = 'convergence_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RelentlessDeps;
  private readonly config: RelentlessConfig;

  /**
   * Each element is the remaining ms until that pending respawn fires.
   * Multiple concurrent timers run independently — one per combat death.
   */
  private _pendingTimers: number[] = [];

  // Observable state for scenarios
  private _respawnCount = 0;
  private _skippedRespawnCount = 0;
  private _lastSpawnX = 0;
  private _lastSpawnY = 0;

  constructor(deps: RelentlessDeps, config: Partial<RelentlessConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Relentless-specific API ---

  /**
   * Handle wisp death. If deathCause is 'combat', enqueues a respawn timer.
   * Detonate, expiry, and Convergence kills are silently ignored.
   *
   * @param _wispId    The dying wisp's ID (not used — all combat deaths
   *                   trigger a timer, regardless of which specific wisp)
   * @param deathCause 'combat' | 'detonate' | 'expiry' | 'converge'
   */
  onWispDied(
    _wispId: string,
    deathCause: 'combat' | 'detonate' | 'expiry' | 'converge',
  ): void {
    if (deathCause !== 'combat') return;

    // Resolve the delay at death time — lower value is the upgraded outcome.
    const delayMs =
      this.deps.resolver.getMinEffect('conjuration', 'autoRespawnDelayMs')
      ?? this.config.autoRespawnDelayMs;

    this._pendingTimers.push(delayMs);
  }

  // --- State getters ---

  /** Total wisps that have been auto-respawned by this passive. */
  get respawnCount(): number { return this._respawnCount; }

  /** Respawn timers that fired but were blocked by the wisp cap. */
  get skippedRespawnCount(): number { return this._skippedRespawnCount; }

  /** X position where the most recent replacement wisp spawned. */
  get lastSpawnX(): number { return this._lastSpawnX; }

  /** Y position where the most recent replacement wisp spawned. */
  get lastSpawnY(): number { return this._lastSpawnY; }

  /** Number of respawn timers currently pending. */
  get pendingTimerCount(): number { return this._pendingTimers.length; }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  get cooldown(): number { return 0; }

  update(delta: number): void {
    if (this._pendingTimers.length === 0) return;

    const stillPending: number[] = [];
    for (const remaining of this._pendingTimers) {
      const newRemaining = remaining - delta;
      if (newRemaining > 0) {
        stillPending.push(newRemaining);
      } else {
        // Timer fired — attempt to spawn a replacement wisp at the player's
        // current position. Cap is evaluated at the moment of spawn, not at
        // the moment of death: if another timer already filled the last slot
        // this tick, this one is silently skipped.
        const wispCount = this.deps.summons.getWispCount();
        const maxWisps = this.deps.summons.getMaxWisps();
        if (wispCount < maxWisps) {
          const pos = this.deps.player.getPosition();
          this.deps.summons.spawnWisp(pos.x, pos.y);
          this._lastSpawnX = pos.x;
          this._lastSpawnY = pos.y;
          this._respawnCount++;
        } else {
          this._skippedRespawnCount++;
        }
      }
    }
    this._pendingTimers = stillPending;
  }

  destroy(): void {
    this._pendingTimers = [];
  }
}
